import { storeToRefs } from 'pinia';
import type { NavigationGuardNext, RouteLocationNormalized, Router } from 'vue-router';

import { useAuthStore, useCompanyStore, useFeatureFlagsStore } from '@/stores';

import RouteName from '@/router/types';

import { loginRequest } from '@/service/auth/config';
import type { IPublicClientApplication, RedirectRequest } from '@azure/msal-browser';
import { proFoundationGuard } from '@mylight150/pro-foundation-app';

export const registerGuard = (router: Router) => {
  router.beforeEach(async (to: RouteLocationNormalized) => {
    const authStore = useAuthStore();
    if (!to.meta.requiresAuth) {
      return true;
    }

    if (authStore.msalInstance === null) {
      return { name: RouteName.LOGIN_FAILED };
    }

    const request = {
      ...loginRequest,
    };
    const shouldProceed = await isAuthenticated(authStore.msalInstance, request);
    if (!shouldProceed) {
      return { name: RouteName.LOGIN_FAILED };
    }

    return await proFoundationGuard(to);
  });
};

export const checkUserCompanies = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const { isTesting } = storeToRefs(useFeatureFlagsStore());
  if (
    (storeToRefs(useAuthStore()).isAuthenticated.value || isTesting.value) &&
    !storeToRefs(useCompanyStore()).currentUserCompany.value?.id
  ) {
    await useCompanyStore().fetchUserCompanies();
  }

  next();
};

export const isAuthenticated = async (
  instance: IPublicClientApplication,
  loginRequest: RedirectRequest
): Promise<boolean> => {
  try {
    await instance.handleRedirectPromise();
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      return true;
    }
    await instance.loginRedirect(loginRequest);
    return true;
  } catch (err) {
    console.warn(err);
    return false;
  }
};
